<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const SHOP_TYPES = [
  'general',
  'small_items',
  'fashion',
  'gadget_mobile',
  'furniture',
  'luxury',
  'service',
];
const NEGOTIATION_MODES = ['value_only', 'max_discount'];

const FIXED_RULE_KEYS = [
  'GREET_ONCE',
  'READ_INTENT',
  'CLARIFY_THEN_SHOW',
  'PHOTO_ONLY',
  'PHOTO_CONFIRM',
  'COMPLAINT',
  'NEGOTIATION',
  'NO_INVENT',
];

const state = reactive({
  shopType: 'general',
  upsellAfterConfirm: true,
  showStockCount: false,
  negotiation: 'value_only',
  maxDiscountAmount: '',
  maxDiscountPercent: '',
});

const selectClass =
  'block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 h-10 px-3 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand';

const shopTypeOptions = computed(() =>
  SHOP_TYPES.map(value => ({
    value,
    label: t(
      `CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.SHOP_TYPE.OPTIONS.${value.toUpperCase()}`
    ),
  }))
);

const negotiationOptions = computed(() => [
  {
    value: 'value_only',
    label: t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.NEGOTIATION.VALUE_ONLY'),
  },
  {
    value: 'max_discount',
    label: t(
      'CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.NEGOTIATION.MAX_DISCOUNT'
    ),
  },
]);

const fixedRules = computed(() =>
  FIXED_RULE_KEYS.map(key =>
    t(`CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.FIXED_RULES.ITEMS.${key}`)
  )
);

const isLuxury = computed(() => state.shopType === 'luxury');

const showDiscountFields = computed(
  () => !isLuxury.value && state.negotiation === 'max_discount'
);

const optionalNumber = value => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const updateStateFromAssistant = assistant => {
  const skill = assistant?.config?.sales_skill || {};
  state.shopType = SHOP_TYPES.includes(skill.shop_type)
    ? skill.shop_type
    : 'general';
  state.upsellAfterConfirm = skill.upsell_after_confirm !== false;
  state.showStockCount = skill.show_stock_count === true;
  if (state.shopType === 'luxury') {
    state.negotiation = 'value_only';
  } else {
    state.negotiation = NEGOTIATION_MODES.includes(skill.negotiation)
      ? skill.negotiation
      : 'value_only';
  }
  state.maxDiscountAmount =
    skill.max_discount_amount === null ||
    skill.max_discount_amount === undefined ||
    skill.max_discount_amount === ''
      ? ''
      : String(skill.max_discount_amount);
  state.maxDiscountPercent =
    skill.max_discount_percent === null ||
    skill.max_discount_percent === undefined ||
    skill.max_discount_percent === ''
      ? ''
      : String(skill.max_discount_percent);
};

const handleShopTypeChange = () => {
  if (state.shopType === 'luxury') {
    state.negotiation = 'value_only';
  }
};

const handleSubmit = () => {
  const negotiation =
    state.shopType === 'luxury' ? 'value_only' : state.negotiation;

  emit('submit', {
    config: {
      sales_skill: {
        shop_type: state.shopType,
        upsell_after_confirm: state.upsellAfterConfirm,
        show_stock_count: state.showStockCount,
        negotiation,
        max_discount_amount: optionalNumber(state.maxDiscountAmount),
        max_discount_percent: optionalNumber(state.maxDiscountPercent),
      },
    },
  });
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <section
      class="flex flex-col gap-3 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <div class="flex flex-col gap-1">
        <h4 class="mb-0 text-base font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.FIXED_RULES.TITLE') }}
        </h4>
        <p class="mb-0 text-sm text-n-slate-11">
          {{
            t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.FIXED_RULES.DESCRIPTION')
          }}
        </p>
      </div>
      <ul class="mb-0 list-disc space-y-1.5 pl-5 text-sm text-n-slate-12">
        <li v-for="rule in fixedRules" :key="rule">
          {{ rule }}
        </li>
      </ul>
    </section>

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.SHOP_TYPE.LABEL') }}
        </label>
        <select
          v-model="state.shopType"
          :class="selectClass"
          @change="handleShopTypeChange"
        >
          <option
            v-for="option in shopTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <p class="mt-1 mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.SHOP_TYPE.HELP') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="flex items-center justify-between gap-4">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.UPSELL.LABEL') }}
          </span>
          <Switch v-model="state.upsellAfterConfirm" />
        </label>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.UPSELL.HELP') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="flex items-center justify-between gap-4">
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.STOCK_COUNT.LABEL') }}
          </span>
          <Switch v-model="state.showStockCount" />
        </label>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.STOCK_COUNT.HELP') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.NEGOTIATION.LABEL') }}
        </label>
        <select
          v-model="state.negotiation"
          :class="selectClass"
          :disabled="isLuxury"
        >
          <option
            v-for="option in negotiationOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <p class="mt-1 mb-0 text-label-small text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.NEGOTIATION.HELP') }}
        </p>
      </div>

      <div
        v-if="showDiscountFields"
        class="grid grid-cols-1 gap-4 md:grid-cols-2"
      >
        <Input
          v-model="state.maxDiscountAmount"
          type="number"
          :label="
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.MAX_DISCOUNT_AMOUNT.LABEL'
            )
          "
          :placeholder="
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.MAX_DISCOUNT_AMOUNT.PLACEHOLDER'
            )
          "
        />
        <Input
          v-model="state.maxDiscountPercent"
          type="number"
          :label="
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.MAX_DISCOUNT_PERCENT.LABEL'
            )
          "
          :placeholder="
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.SALES_SKILL.MAX_DISCOUNT_PERCENT.PLACEHOLDER'
            )
          "
        />
      </div>
    </section>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
